import { Button } from 'react-bootstrap';
import { Navigate, useParams } from 'react-router-dom';

import ErrorLabel from '../../../ui/ErrorLabel';

import BaseConnectionContainer from './BaseConnectionContainer';
import { inverterManufacturers } from './inverterManufacturers';

export interface ConnectionContainerProps {
  /** Manufacturer slug, e.g. "givenergy". Falls back to the `:manufacturer` route param when omitted. */
  manufacturer?: string;
  mode?: 'onboarding' | 'edit';
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Shown as a "choose a different manufacturer" link when the manufacturer step was skipped. */
  onBack?: () => void;
}

/**
 * Generic connect-to-inverter form, driven by the `inverterManufacturers` config
 * instead of a hardcoded component per manufacturer.
 */
const ConnectionContainer = ({ manufacturer, ...rest }: ConnectionContainerProps) => {
  const { manufacturer: manufacturerParam } = useParams<{ manufacturer: string }>();
  const usingRouteParam = manufacturer === undefined;
  const slug = manufacturer ?? manufacturerParam;
  const config = slug ? inverterManufacturers[slug] : undefined;

  if (!config) {
    // Preserve the onboarding route's original 404 fallback for unrecognised manufacturers.
    if (usingRouteParam) {
      return <Navigate to="/onboarding/not-found" replace />;
    }

    // Defensive fallback for a prop-provided slug we don't recognise (e.g. stale onboarding
    // data) - never leave the user in a blank modal with no visible way out.
    return (
      <div>
        <ErrorLabel label="We couldn't find a connection form for that inverter manufacturer." />
        {rest.onCancel && (
          <Button variant="light" type="button" onClick={rest.onCancel} className="mt-3 w-100">
            Cancel
          </Button>
        )}
      </div>
    );
  }

  return (
    <BaseConnectionContainer
      fields={config.fields}
      inverterManufacturer={config.manufacturer}
      exampleModalContent={config.exampleModalContent}
      logo={config.logo}
      {...rest}
    />
  );
};

export default ConnectionContainer;
